import { CommandBarButton, IContextualMenuItem, mergeStyles, useTheme } from "@fluentui/react";
import { ChevronRightIcon } from "@fluentui/react-icons-mdl2";
import { EMergeVMStatus } from "global/Compare/ComparisonViewModelGenerator";
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

// ============================================================================
// Constants
// ============================================================================

const HEADER_MIN_HEIGHT = '45px';
const CONTENT_PADDING = '5px';
const CONTAINER_BORDER = '1px solid rgba(180, 180, 180, 0.4)';
const CHEVRON_MARGIN_LEFT = '15px';
const CHEVRON_TRANSITION = '180ms ease all';
const CHEVRON_ROTATION = 'rotate(90deg)';
const TITLE_MARGIN_COLLAPSIBLE = '5px';
const TITLE_MARGIN_DEFAULT = '15px';
const FONT_WEIGHT_COLLAPSED = 300;
const FONT_WEIGHT_EXPANDED = 450;
const DEFAULT_STICKY_Z_INDEX = 1;

const CHEVRON_ICON_STYLE: React.CSSProperties = { fontSize: 'smaller' };

const CONTENT_COLLAPSED: React.CSSProperties = {
    opacity: 0,
    maxHeight: 0,
    display: 'none',
    overflow: 'hidden',
};

const CONTENT_EXPANDED: React.CSSProperties = {
    opacity: 1,
    maxHeight: 'none',
    overflow: 'visible',
};

const YELLOW_HEADER_BG = '#fffab2';
const YELLOW_CONTENT_BG = '#fffdf5';
const YELLOW_BORDER = '#f7c948';
const YELLOW_HEADER_BG_DARK = '#4a3a00';
const YELLOW_CONTENT_BG_DARK = '#2f2600';
const YELLOW_BORDER_DARK = '#d6a600';

const GREEN_HEADER_BG = '#dff6dd';
const GREEN_CONTENT_BG = '#f4fbf2';
const GREEN_BORDER = '#6bb700';
const GREEN_HEADER_BG_DARK = '#163a16';
const GREEN_CONTENT_BG_DARK = '#102910';
const GREEN_BORDER_DARK = '#78c257';

const RED_HEADER_BG = '#fde7e9';
const RED_CONTENT_BG = '#fff5f5';
const RED_BORDER = '#d13438';
const RED_HEADER_BG_DARK = '#4a1f23';
const RED_CONTENT_BG_DARK = '#33161a';
const RED_BORDER_DARK = '#ff7d83';

const GRAY_HEADER_BG = '#f3f2f1';
const GRAY_CONTENT_BG = '#f5f5f5';
const GRAY_BORDER = '#a19f9d';
const GRAY_HEADER_BG_DARK = '#2d2d2d';
const GRAY_CONTENT_BG_DARK = '#1f1f1f';
const GRAY_BORDER_DARK = '#8a8886';

const ORANGE_HEADER_BG = '#ffe4cc';
const ORANGE_CONTENT_BG = '#fff7f0';
const ORANGE_BORDER = '#da8a3b';
const ORANGE_HEADER_BG_DARK = '#4a2f1a';
const ORANGE_CONTENT_BG_DARK = '#331f0d';
const ORANGE_BORDER_DARK = '#ff9d3d';

const ALLOWED_TOGGLE_KEYS = new Set(['Space', 'Enter', 'ArrowRight']);

// ============================================================================
// Helpers
// ============================================================================

export const getPanelStyleTypeFromMergeAction = (
    isMergeApp: boolean | undefined,
    mergeAction: EMergeVMStatus
): EPanelStyleType => {

    if (isMergeApp) {
        switch (mergeAction) {
            case EMergeVMStatus.AddOrigin:
                return EPanelStyleType.Green;

            case EMergeVMStatus.UseOrigin:
                return EPanelStyleType.Orange;

            case EMergeVMStatus.RemoveTarget:
                return EPanelStyleType.Red;

            case EMergeVMStatus.Merge:
                return EPanelStyleType.Yellow;

            case EMergeVMStatus.UseTarget:
                return EPanelStyleType.Gray;

            case EMergeVMStatus.Disabled:
                return EPanelStyleType.Default;

            case EMergeVMStatus.Pend:
                return EPanelStyleType.Default;

            case EMergeVMStatus.ParentOverride:
            case EMergeVMStatus.ParentOverrideUseOrigin:
            case EMergeVMStatus.ParentOverrideUseTarget:
                return EPanelStyleType.Default;

            default:
                const exhaustiveCheck: never = mergeAction;
                throw new Error(`Unhandled merge action: ${exhaustiveCheck}`);
        }
    }

    return EPanelStyleType.Default;
};



// ============================================================================
// Types
// ============================================================================

export enum EPanelStyleType {
    Default,
    Yellow,
    Green,
    Red,
    Gray,
    Orange,
}

export interface ICollapsiblePanelProperties {
    id?: string;
    title?: ReactNode;
    headerRight?: ReactNode;
    headerRightMenuItems?: IContextualMenuItem[];
    children?: ReactNode;
    defaultCollapsed?: boolean;
    collapsed?: boolean;
    onCollapseChange?: (status: boolean) => void;
    contentStyle?: React.CSSProperties;
    headerStyle?: React.CSSProperties;
    style?: React.CSSProperties;
    canCollapse?: boolean;
    disabled?: boolean;
    stickyHeader?: boolean;
    stickyTop?: number;
    stickyZIndex?: number;
    styleType?: EPanelStyleType;
    collapseUnmounts?: boolean;
    'data-tiers'?: string;
    'data-properties'?: string;
    'data-references'?: string;
    'data-section-id'?: string;
    'data-tab-id'?: string;
    'data-category'?: string;
    'data-category-id'?: string;
    'data-attribute-set-id'?: string;
    'data-attribute-set-config-master-attribute-id'?: string;
    'data-nonstandard-id'?: string;
    'data-nonstandard-section-id'?: string;
}

// ============================================================================
// Component
// ============================================================================

const CustomPanel: React.FC<ICollapsiblePanelProperties> = (props) => {
    const theme = useTheme();
    const isDarkTheme = !!theme.isInverted;
    const [localCollapsed, setLocalCollapsed] = useState<boolean>(props.defaultCollapsed ?? false);

    const [hasDelayedMount, setHasDelayedMount] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setHasDelayedMount(false);
        }, 25 + Math.trunc(Math.random() * 150));

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        setLocalCollapsed(props.defaultCollapsed ?? false);
    }, [props.defaultCollapsed]);

    const isCollapsed = props.collapsed ?? localCollapsed;

    const onToggle = useCallback(() => {
        if (!props.canCollapse || props.disabled) return;

        const nextState = !isCollapsed;
        setLocalCollapsed(nextState);
        props.onCollapseChange?.(nextState);
    }, [props.disabled, isCollapsed, props.canCollapse, props.onCollapseChange]);

    const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (ALLOWED_TOGGLE_KEYS.has(event.code)) {
            event.preventDefault();
            onToggle();
        }
    }, [onToggle]);

    const onMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (!props.canCollapse || props.disabled) return;

        // Prevent browser text selection when rapidly clicking to expand/collapse.
        event.preventDefault();
    }, [props.canCollapse, props.disabled]);

    // Memoize the header class to avoid re-running mergeStyles on every render
    const headerClassName = useMemo(() => mergeStyles({
        ...(props.styleType === EPanelStyleType.Default ? {
            backgroundColor: theme.semanticColors.menuBackground,
        } : {}),
        ...(props.styleType === EPanelStyleType.Yellow ? {
            backgroundColor: isDarkTheme ? YELLOW_HEADER_BG_DARK : YELLOW_HEADER_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Green ? {
            backgroundColor: isDarkTheme ? GREEN_HEADER_BG_DARK : GREEN_HEADER_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Red ? {
            backgroundColor: isDarkTheme ? RED_HEADER_BG_DARK : RED_HEADER_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Gray ? {
            backgroundColor: isDarkTheme ? GRAY_HEADER_BG_DARK : GRAY_HEADER_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Orange ? {
            backgroundColor: isDarkTheme ? ORANGE_HEADER_BG_DARK : ORANGE_HEADER_BG,
        } : {}),
        color: theme.semanticColors.bodyText,
        fontSize: theme.fonts.mediumPlus.fontSize,
        cursor: props.canCollapse ? 'pointer' : 'default',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        minHeight: HEADER_MIN_HEIGHT,
        ':hover': {
            ...(props.canCollapse ? {
                backgroundColor: theme.semanticColors.menuItemBackgroundHovered,
            } : {}),
        },
    }), [theme, props.canCollapse, props.styleType, isDarkTheme]);

    // Memoize sticky style object
    const headerInlineStyle = useMemo<React.CSSProperties>(() => {

        return {
            backgroundColor: theme.semanticColors.menuBackground,
            borderRadius: '10px',
            borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
            ...(props.stickyHeader ? {
                position: 'sticky',
                top: props.stickyTop ?? 0,
                zIndex: props.stickyZIndex ?? DEFAULT_STICKY_Z_INDEX,
                isolation: 'isolate',
            } : {}),
            ...(props.styleType === EPanelStyleType.Default ? {
            } : {}),
            ...(props.styleType === EPanelStyleType.Yellow ? {
                backgroundColor: isDarkTheme ? YELLOW_HEADER_BG_DARK : YELLOW_HEADER_BG,
            } : {}),
            ...(props.styleType === EPanelStyleType.Green ? {
                backgroundColor: isDarkTheme ? GREEN_HEADER_BG_DARK : GREEN_HEADER_BG,
            } : {}),
            ...(props.styleType === EPanelStyleType.Red ? {
                backgroundColor: isDarkTheme ? RED_HEADER_BG_DARK : RED_HEADER_BG,
            } : {}),
            ...(props.styleType === EPanelStyleType.Gray ? {
                backgroundColor: isDarkTheme ? GRAY_HEADER_BG_DARK : GRAY_HEADER_BG,
            } : {}),
            ...(props.styleType === EPanelStyleType.Orange ? {
                backgroundColor: isDarkTheme ? ORANGE_HEADER_BG_DARK : ORANGE_HEADER_BG,
            } : {}),
        };
    }, [
        theme,
        props.stickyHeader,
        props.stickyTop,
        props.stickyZIndex,
        props.styleType,
        isDarkTheme,
    ]);

    // Memoize container style
    const containerStyle = useMemo<React.CSSProperties>(() => ({
        position: 'relative',
        border: CONTAINER_BORDER,
        ...props.style,
        borderRadius: '10px',
        ...(props.styleType === EPanelStyleType.Default ? {
            borderLeft: `1px solid rgba(180, 180, 180, 0.6)`,
        } : {}),
        ...(props.styleType === EPanelStyleType.Yellow ? {
            border: `3px solid ${isDarkTheme ? YELLOW_BORDER_DARK : YELLOW_BORDER}`,

            backgroundColor: isDarkTheme ? YELLOW_CONTENT_BG_DARK : YELLOW_CONTENT_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Green ? {
            border: `3px solid ${isDarkTheme ? GREEN_BORDER_DARK : GREEN_BORDER}`,

            backgroundColor: isDarkTheme ? GREEN_CONTENT_BG_DARK : GREEN_CONTENT_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Red ? {
            border: `3px solid ${isDarkTheme ? RED_BORDER_DARK : RED_BORDER}`,

            backgroundColor: isDarkTheme ? RED_CONTENT_BG_DARK : RED_CONTENT_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Gray ? {
            border: `3px solid ${isDarkTheme ? GRAY_BORDER_DARK : GRAY_BORDER}`,

            backgroundColor: isDarkTheme ? GRAY_CONTENT_BG_DARK : GRAY_CONTENT_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Orange ? {
            border: `3px solid ${isDarkTheme ? ORANGE_BORDER_DARK : ORANGE_BORDER}`,

            backgroundColor: isDarkTheme ? ORANGE_CONTENT_BG_DARK : ORANGE_CONTENT_BG,
        } : {}),
    }), [
        props.style,
        props.styleType,
        isDarkTheme,
    ]);

    // Memoize chevron style
    const chevronStyle = useMemo<React.CSSProperties>(() => ({
        marginLeft: CHEVRON_MARGIN_LEFT,
        transition: CHEVRON_TRANSITION,
        transform: !isCollapsed ? CHEVRON_ROTATION : '',
    }), [isCollapsed]);

    // Memoize title wrapper style
    const titleStyle = useMemo<React.CSSProperties>(() => ({
        marginLeft: props.canCollapse ? TITLE_MARGIN_COLLAPSIBLE : TITLE_MARGIN_DEFAULT,
        fontWeight: isCollapsed ? FONT_WEIGHT_COLLAPSED : FONT_WEIGHT_EXPANDED,
        ...props.headerStyle,

    }), [props.canCollapse, isCollapsed, props.headerStyle]);

    // Memoize content style
    const computedContentStyle = useMemo<React.CSSProperties>(() => ({
        padding: CONTENT_PADDING,
        paddingLeft: '10px',
        paddingRight: '10px',
        position: 'relative',
        borderRadius: '10px',
        zIndex: 0,
        ...(props.styleType === EPanelStyleType.Yellow ? {
            backgroundColor: isDarkTheme ? YELLOW_CONTENT_BG_DARK : YELLOW_CONTENT_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Green ? {
            backgroundColor: isDarkTheme ? GREEN_CONTENT_BG_DARK : GREEN_CONTENT_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Red ? {
            backgroundColor: isDarkTheme ? RED_CONTENT_BG_DARK : RED_CONTENT_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Gray ? {
            backgroundColor: isDarkTheme ? GRAY_CONTENT_BG_DARK : GRAY_CONTENT_BG,
        } : {}),
        ...(props.styleType === EPanelStyleType.Orange ? {
            backgroundColor: isDarkTheme ? ORANGE_CONTENT_BG_DARK : ORANGE_CONTENT_BG,
        } : {}),
        ...(isCollapsed ? CONTENT_COLLAPSED : CONTENT_EXPANDED),
        ...props.contentStyle,

    }), [isCollapsed, props.contentStyle, props.styleType, isDarkTheme]);


    let isMounted = true;
    let _collapseUnmounts = props.collapseUnmounts ?? false;

    if (hasDelayedMount)
        isMounted = false;

    // temporary to test
    // _collapseUnmounts = true;

    if (_collapseUnmounts && isCollapsed)
        isMounted = false;

    return (
        <div
            id={props.id}
            style={containerStyle}
            data-category={props['data-category']}
            data-tiers={props['data-tiers']}
            data-properties={props['data-properties']}
            data-references={props['data-references']}
            data-tab-id={props['data-tab-id']}
            data-section-id={props['data-section-id']}
            data-attribute-set-id={props['data-attribute-set-id']}
            data-attribute-set-config-master-attribute-id={props['data-attribute-set-config-master-attribute-id']}
            data-nonstandard-id={props['data-nonstandard-id']}
            data-nonstandard-section-id={props['data-nonstandard-section-id']}
            data-category-id={props['data-category-id']}
        >
            <div
                className={headerClassName}
                style={headerInlineStyle}
                role="button"
                tabIndex={0}
                aria-expanded={!isCollapsed}
                data-collapsed={isCollapsed}
                onClick={onToggle}
                onMouseDown={onMouseDown}
                onKeyDown={onKeyDown}
            >
                {props.canCollapse && (
                    <div style={chevronStyle}>
                        <ChevronRightIcon style={CHEVRON_ICON_STYLE} />
                    </div>
                )}

                <div style={titleStyle}>
                    {props.title}
                </div>

                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', marginRight: '4px' }}>
                    {props.headerRightMenuItems?.map((item) => (
                        <CommandBarButton
                            key={item.key}
                            iconProps={{ iconName: item.icon }}
                            text={item.name ?? item.text}
                            title={item.title ?? item.name ?? item.text}
                            ariaLabel={item.ariaLabel ?? item.name ?? item.text}
                            disabled={item.disabled}
                            menuProps={item.subMenuProps}
                            styles={{ root: { backgroundColor: 'transparent' } }}
                            onKeyDown={(event) => event.stopPropagation()}
                            onClick={(event: any) => {
                                event.stopPropagation();
                                item.onClick?.(event, item);
                            }}
                        />
                    ))}
                    {props.headerRight && (
                        props.headerRight
                    )}
                </div>
            </div>
            {isMounted && (
                <div style={computedContentStyle}>
                    {props.children}
                </div>
            )}
        </div>
    );
};

export default CustomPanel;
export const CustomPanelWithMemo = React.memo(CustomPanel);
